import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import type { IntakeSettings } from '@esa/shared';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Callout, Checkbox, Field, Input, Panel, Textarea, type Tone } from '../../ui/index.js';

/**
 * Configuration → Intake → Test the assistant.
 *
 * Type a message and see what intake would do with it, using the settings on
 * this screen AS THEY ARE NOW — saved or not — so scope, tone and decline
 * wording can be tuned by trying them. Runs the same decision real mail goes
 * through; nothing is sent, filed or recorded.
 */

interface Result {
  desk: string;
  from: string;
  outcome: 'filed' | 'declined' | 'ignored';
  screen: { accept: true } | { accept: false; phase: string; rule: string; reason: string };
  isReply: boolean;
  piiFound: string[];
  piiBlocked: string[];
  modelAvailable: boolean;
  modelAsked: boolean;
  model: { inappropriate: boolean; inScope: boolean; why: string } | null;
  guard: { action: 'file'; note: string | null } | { action: 'decline'; reason: string; detail: string } | null;
  declineReply: { subject: string; text: string } | null;
  knowledge: {
    retrieval: string;
    note: string | null;
    articles: { title: string; confidence: string; snippet: string; url: string | null }[];
    floor: number | null;
    belowFloor: { title: string; score: number }[];
  } | null;
  /** Where it would be routed, and why. Null for a reply. */
  route?: {
    queue: { key: string; name: string } | null;
    routedBy: 'chosen' | 'subject:confidential' | 'rule' | 'subject' | 'ladder' | 'default';
    confidence: number | null;
    why: string | null;
    confidential: boolean;
    /** The request type the model read it as, and the desk rules' handling (075). */
    requestType?: string | null;
    typeBy?: 'chosen' | 'subject' | 'ladder' | null;
    typeConfidence?: number | null;
    handling?: {
      priority: 'low' | 'medium' | 'high' | 'critical' | null;
      holdForPerson: boolean;
      keepOpen: boolean;
      notify: string[];
      rules: string[];
    };
  } | null;
  receives:
    | { kind: 'answer' | 'partial' | 'acknowledgement' | 'decline'; subject: string; text: string }
    | { kind: 'nothing'; subject: null; text: null };
  assistant: {
    switchedOn: boolean;
    modelAvailable: boolean;
    tried: boolean;
    answered: boolean;
    why: string | null;
    cited: { title: string; score: number }[];
    /** For a partial answer: what is left for a person. */
    outstanding?: string[];
    considered: { title: string; score: number; rating?: Rating; ratingWhy?: string }[];
  };
  /** Every model call as sent, and what came back. */
  calls: { step: 'guard' | 'answer'; system: string; user: string; raw: string | null }[];
}

type Rating = 'answers' | 'partly' | 'no';
const RATING: Record<Rating, { label: string; tone: Tone }> = {
  answers: { label: 'answers it', tone: 'ok' },
  partly: { label: 'partly', tone: 'warn' },
  no: { label: 'no', tone: 'neutral' },
};

const CALL_TITLE: Record<Result['calls'][number]['step'], string> = {
  guard: 'Language and scope check',
  answer: 'Answering from the knowledgebase',
};

/** The model's reply, indented when it is the JSON it was asked for. */
function pretty(raw: string | null): string {
  if (raw === null) return '(no reply)';
  const m = /\{[\s\S]*\}/.exec(raw);
  if (!m) return raw;
  try {
    return JSON.stringify(JSON.parse(m[0]), null, 2);
  } catch {
    return raw;
  }
}

function Block({ title, text }: { title: string; text: string }) {
  return (
    <details className="mt-1.5">
      <summary className="cursor-pointer text-2xs text-ink-secondary hover:text-ink">{title}</summary>
      <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-surface-sunken px-2 py-1.5 font-mono text-3xs leading-relaxed text-ink-secondary">
        {text}
      </pre>
    </details>
  );
}

/** Collapsed: what the model was sent and what it said, for the few who want it. */
function ModelDetails({ calls }: { calls: Result['calls'] }) {
  if (calls.length === 0) return null;
  return (
    <details className="border-t border-line-subtle pt-2">
      <summary className="cursor-pointer text-2xs text-ink-tertiary hover:text-ink-secondary">
        Show details — what the model was sent ({calls.length === 1 ? '1 call' : `${calls.length} calls`})
      </summary>
      <div className="mt-2 space-y-3">
        {calls.map((c, i) => (
          <div key={i}>
            <div className="text-2xs font-semibold text-ink">{CALL_TITLE[c.step]}</div>
            <Block title="The fixed rules (the same for every desk)" text={c.system} />
            <Block title="What it was given — this desk's settings, the articles, the message" text={c.user} />
            <Block title="What it replied" text={pretty(c.raw)} />
          </div>
        ))}
      </div>
    </details>
  );
}

/** The headline: what happens, in one line. */
function headline(r: Result): { label: string; tone: Tone; says: string } {
  if (r.outcome === 'ignored') return { label: 'Ignored', tone: 'neutral', says: 'Treated as automated mail: logged, and nothing is sent.' };
  if (r.outcome === 'declined') return { label: 'Declined', tone: 'warn', says: 'No ticket is opened. They get the reply below.' };
  if (r.outcome === 'filed' && r.route?.confidential)
    return {
      label: 'Confidential',
      tone: 'warn',
      says: `A ticket is opened in ${r.route.queue?.name ?? 'a restricted queue'}, which only its members can see. It is never answered from the knowledgebase.`,
    };
  if (r.outcome === 'filed' && !r.isReply && r.route?.handling?.holdForPerson)
    return {
      label: 'Held for a person',
      tone: 'warn',
      says: `A ticket is opened and acknowledged, and the assistant stays out of it: ${r.route.handling.rules.map((n) => `“${n}”`).join(', ')} holds it for a person.`,
    };
  if (r.isReply) return { label: 'Added to their ticket', tone: 'ok', says: 'A reply on an existing ticket: it joins that ticket and nothing is sent back.' };
  if (r.assistant.answered) return { label: 'Answered', tone: 'ok', says: 'A ticket is opened, answered with the reply below, and resolved. If they reply, it reopens.' };
  if (r.receives.kind === 'partial')
    return {
      label: 'Answered in part',
      tone: 'info',
      says: `A ticket is opened and answered with the reply below, and stays open for a person: ${(r.assistant.outstanding ?? []).join('; ')}.`,
    };
  return { label: 'Filed for the desk', tone: 'info', says: 'A ticket is opened for a person to pick up.' };
}

const RECEIVES_LABEL: Record<Result['receives']['kind'], string> = {
  answer: 'The answer they would get',
  partial: 'The partial answer they would get',
  acknowledgement: 'The acknowledgement they would get',
  decline: 'The reply they would get',
  nothing: 'What they would get',
};

const ROUTED_BY: Record<NonNullable<Result['route']>['routedBy'], string> = {
  chosen: 'the queue that was chosen',
  'subject:confidential': 'it is about what this restricted queue is for',
  rule: 'a routing rule sent it there',
  subject: 'it is about what this queue is for',
  ladder: 'the email classification sent it there',
  default: 'no queue description fitted closely enough',
};

const REASON: Record<string, string> = {
  personalData: 'personal data',
  nsfw: 'inappropriate language',
  outOfScope: 'not something this desk handles',
};

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="grid grid-cols-[1.5rem_1fr] gap-2">
      <span className="font-mono text-2xs text-ink-tertiary">{n}.</span>
      <div>
        <div className="text-2xs font-semibold text-ink">{title}</div>
        <div className="text-2xs leading-relaxed text-ink-secondary">{children}</div>
      </div>
    </li>
  );
}

export function TestAssistantPanel({
  tenant,
  intake,
  blocked,
  unsaved = false,
}: {
  tenant: string;
  /** The settings on screen now, saved or not. */
  intake: IntakeSettings;
  /** Why the settings cannot be tested (they could not be saved either). */
  blocked: string | null;
  /** The settings on screen differ from what is saved: say so, so a good test is not mistaken for a live desk. */
  unsaved?: boolean;
}) {
  // Starts as the signed-in person's address — the realistic sender, and
  // editable to try someone else. Filled once it loads, never over an edit.
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<{ email?: string }>('/api/v1/me') });
  const [from, setFrom] = useState('');
  const [fromTouched, setFromTouched] = useState(false);
  useEffect(() => {
    if (!fromTouched && me.data?.email) setFrom(me.data.email);
  }, [me.data?.email, fromTouched]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isReply, setIsReply] = useState(false);
  const run = useMutation({
    mutationFn: () =>
      api.post<Result>(`/api/v1/tenants/${tenant}/admin/intake/test`, {
        ...(from.trim() ? { from: from.trim() } : {}),
        subject,
        body,
        isReply,
        intake,
      }),
  });
  const r = run.data;

  return (
    <Panel
      title="Test the assistant"
      subtitle="Try a message against the settings on this screen, saved or not. Nothing is sent, filed or recorded."
    >
      {unsaved && (
        <Callout tone="warn" className="mb-2.5">
          Testing <strong>unsaved</strong> changes. Real requests still get the saved settings — save to make these live.
        </Callout>
      )}
      <div className="grid gap-2.5 sm:grid-cols-2">
        <Field label="From" hint="You, by default. Change it to try someone else.">
          <Input
            value={from}
            onChange={(e) => {
              setFromTouched(true);
              setFrom(e.target.value);
            }}
            placeholder="someone@esassoc.com"
            aria-label="Test from"
          />
        </Field>
        <Field label="Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="My Z drive has disappeared" aria-label="Test subject" />
        </Field>
      </div>
      <Field label="Message" className="mt-2.5">
        <Textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write it the way somebody would actually write it."
          aria-label="Test message"
        />
      </Field>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <Checkbox
          checked={isReply}
          onCheckedChange={setIsReply}
          label="A reply on an existing ticket"
          hint="Replies are not judged for scope."
        />
        <Button
          variant="primary"
          leading={<FlaskConical />}
          onClick={() => run.mutate()}
          loading={run.isPending}
          disabled={Boolean(blocked) || (!subject.trim() && !body.trim())}
        >
          Run test
        </Button>
      </div>
      {blocked && <p className="mt-2 text-2xs text-danger">Fix the settings above first — {blocked}</p>}
      {run.error && (
        <p className="mt-2 text-2xs text-danger">
          {run.error instanceof ApiError ? run.error.message : 'The test could not run.'}
        </p>
      )}

      {r && (() => {
        const h = headline(r);
        return (
        <div className="mt-4 space-y-3 border-t border-line-subtle pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={h.tone}>{h.label}</Badge>
            <span className="text-2xs text-ink-secondary">{h.says}</span>
          </div>

          <div className="rounded-md border border-line bg-surface p-3">
            <div className="text-3xs font-semibold uppercase tracking-wide text-ink-tertiary">{RECEIVES_LABEL[r.receives.kind]}</div>
            {r.receives.kind === 'nothing' ? (
              <p className="mt-1 text-2xs text-ink-secondary">
                Nothing is sent.
                {r.outcome === 'filed' && !r.isReply ? ' The acknowledgement is switched off for this desk.' : ''}
              </p>
            ) : (
              <>
                <div className="mt-1 text-2xs font-medium text-ink">{r.receives.subject}</div>
                <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-ink">{r.receives.text}</p>
                {(r.receives.kind === 'answer' || r.receives.kind === 'partial') && r.assistant.cited.length > 0 && (
                  <div className="mt-2 border-t border-line-subtle pt-2 text-3xs text-ink-tertiary">
                    Answered from: {r.assistant.cited.map((c) => `${c.title} (${Math.round(c.score * 100)}%)`).join(' · ')}
                  </div>
                )}
              </>
            )}
          </div>

          <ol className="space-y-2">
            <Step n={1} title="Screening — is this from a person?">
              {r.screen.accept ? `Passed. From ${r.from}.` : `Ignored: ${r.screen.reason}.`}
            </Step>
            {r.screen.accept && (
              <Step n={2} title="Personal data">
                {r.piiFound.length === 0
                  ? 'None found.'
                  : r.piiBlocked.length > 0
                    ? `Found ${r.piiFound.join(', ')} — this desk declines ${r.piiBlocked.join(', ')}.`
                    : `Found ${r.piiFound.join(', ')} — allowed by this desk's settings.`}
              </Step>
            )}
            {r.screen.accept && (
              <Step n={3} title="Language and scope">
                {!r.modelAvailable
                  ? 'Not checked: no model is configured, so messages are filed for a person.'
                  : !r.modelAsked
                    ? 'Not needed: personal data already decided it.'
                    : !r.model
                      ? 'The model did not give a clear answer, so the message would be filed for a person.'
                      : [
                          r.model.inappropriate ? 'Inappropriate language.' : 'Language is fine.',
                          r.isReply ? 'Scope not judged — it is a reply.' : r.model.inScope ? 'Within this desk’s scope.' : 'Outside this desk’s scope.',
                          r.model.why ? `“${r.model.why}”` : '',
                        ].filter(Boolean).join(' ')}
              </Step>
            )}
            {r.route && r.outcome === 'filed' && (
              <Step n={4} title="Where it goes">
                {r.route.queue
                    ? `${r.route.queue.name}${r.route.confidential ? ' (restricted)' : ''}: ${ROUTED_BY[r.route.routedBy]}`
                    : `The default queue, for a person to route: ${ROUTED_BY[r.route.routedBy]}`}
                {r.route.confidence !== null ? ` · ${Math.round(r.route.confidence * 100)}% sure` : ''}
                {r.route.why ? ` · “${r.route.why}”` : ''}
                <br />
                {r.route.requestType
                  ? `Request type: ${r.route.requestType}${r.route.typeConfidence ? ` (${Math.round(r.route.typeConfidence * 100)}% sure)` : ''}.`
                  : 'Request type: none chosen. Only types with a description can be read here; real email also has the keyword ladder.'}
                {r.route.handling && r.route.handling.rules.length > 0 && (
                  <>
                    <br />
                    {`Handled by ${r.route.handling.rules.map((n) => `“${n}”`).join(', ')}: ${[
                      r.route.handling.priority ? `priority ${r.route.handling.priority}` : '',
                      r.route.handling.holdForPerson ? 'the assistant stays out' : '',
                      r.route.handling.keepOpen ? 'never closes by itself' : '',
                      r.route.handling.notify.length ? `alerts ${r.route.handling.notify.join(', ')}` : '',
                    ]
                      .filter(Boolean)
                      .join(', ')}.`}
                  </>
                )}
              </Step>
            )}
            {r.outcome === 'filed' && !r.isReply && (
              <Step n={5} title="Answering from the knowledgebase">
                {!r.assistant.switchedOn
                  ? 'Off for this desk — Assistant answers is switched off on the Features tab, so it is filed for a person.'
                  : r.route?.handling?.holdForPerson
                    ? 'Not tried: a routing rule holds it for a person.'
                  : !r.assistant.modelAvailable
                    ? 'Not tried: no model is configured.'
                    : r.assistant.answered
                      ? `Answered.${r.assistant.why ? ` “${r.assistant.why}”` : ''}`
                      : `Not answered: ${r.assistant.why ?? 'nothing suitable'}.`}
                {r.assistant.considered.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {r.assistant.considered.map((c) => (
                      <li key={c.title} className="flex items-center justify-between gap-2 text-ink-tertiary">
                        <span className="truncate" title={c.ratingWhy || undefined}>{c.title}</span>
                        <span className="flex shrink-0 items-center gap-1.5">
                          {c.rating && <Badge tone={RATING[c.rating].tone}>{RATING[c.rating].label}</Badge>}
                          <span className="nums">{Math.round(c.score * 100)}%</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Step>
            )}
          </ol>

          <ModelDetails calls={r.calls ?? []} />
        </div>
        );
      })()}
    </Panel>
  );
}
